import React, { useState, useEffect } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import './App.css';
import { Helmet } from 'react-helmet';
import emailjs from '@emailjs/browser';
import Posts from './Posts';
import Stories from './Stories';
import Write from './Write';
import useFadeInOnScroll from './useFadeInOnScroll';
import ProjectCard, { DEFAULT_PROJECTS } from './ProjectCard';

const Navigation = ({ isDarkMode, toggleDarkMode, showContactCTA, hasPosts, hasStories }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <nav className="navbar">
      <div className="nav-content">
        <button 
          className="hamburger-menu-btn"
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
        <ul className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
          <li><img src="/logo/logoCommandLine0.png" width="40" height="40" alt="logo" style={{verticalAlign: 'middle'}}/></li>
          <li><a href="/#home" onClick={() => setIsMenuOpen(false)}>About Me</a></li>
          <li><a href="/#projects" onClick={() => setIsMenuOpen(false)}>Projects</a></li>
          <li><a href="/#experience" onClick={() => setIsMenuOpen(false)}>Experience</a></li>
          {hasPosts && <li><Link to="/posts" onClick={() => setIsMenuOpen(false)}>Posts</Link></li>}
          {hasStories && <li><Link to="/stories" onClick={() => setIsMenuOpen(false)}>Stories</Link></li>}
          <li><a href="/RobertThomas.pdf" target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)}>Resume</a></li>
          <li>
            <button 
              className="theme-toggle" 
              onClick={toggleDarkMode}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/>
                  <line x1="12" y1="1" x2="12" y2="3"/>
                  <line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/>
                  <line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>
          </li>
        </ul>
        {/* Contact Me CTA on desktop - icon only when visible */}
        <a 
          href="#contact" 
          className={`navbar-contact-cta${showContactCTA ? ' visible' : ''}`}
          style={{
            pointerEvents: showContactCTA ? 'auto' : 'none',
            width: 48,
            minWidth: 0,
            padding: 0,
            overflow: 'hidden',
            transition: 'width 0.3s, padding 0.3s',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          tabIndex={showContactCTA ? 0 : -1}
          aria-label="Contact Me"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        </a>
      </div>
    </nav>
  );
};

const MobileHeaderCard = () => {
  const fadeRef = useFadeInOnScroll();
  return (
    <section className="mobile-header-section mobile-only" ref={fadeRef}>
      <h1 className="mobile-header-name">Robert Thomas</h1>
      <p className="hero-focus">Embedded systems and firmware</p>
      <p className="hero-stack">bare-metal C++ / ESP32-S3 / KiCad / PlatformIO</p>
      <h2 className="mobile-header-title">Electrical/Mechatronics Engineer I at EPRI</h2>
      <div className="hero-links">
        <a href="https://github.com/robertengineerr" target="_blank" rel="noopener noreferrer">GitHub</a>
        <a href="/RobertThomas.pdf" target="_blank" rel="noopener noreferrer">Resume</a>
        <a href="mailto:me@robertengineer.com">Email</a>
      </div>
    </section>
  );
};

const Header = () => {
  const fadeRef = useFadeInOnScroll();
  return (
    <header className="header desktop-only" ref={fadeRef}>
      <div className="header-content">
        <div className="header-left">
          <h1>Robert Thomas</h1>
          <p className="hero-focus">Embedded systems and firmware</p>
          <p className="hero-stack">bare-metal C++ / ESP32-S3 / KiCad / PlatformIO</p>
          <div className="hero-links">
            <a href="https://github.com/robertengineerr" target="_blank" rel="noopener noreferrer">GitHub</a>
            <a href="/RobertThomas.pdf" target="_blank" rel="noopener noreferrer">Resume</a>
            <a href="mailto:me@robertengineer.com">me@robertengineer.com</a>
          </div>
        </div>
        <div className="header-right">
          <h2>Electrical/Mechatronics Engineer I<br></br>Electric Power Research Institute (EPRI)</h2>
        </div>
      </div>
    </header>
  );
};

const Projects = () => {
  const fadeRef = useFadeInOnScroll();
  const [pinned, setPinned] = useState(null);

  useEffect(() => {
    fetch('/posts.json', { cache: 'no-store' })
      .then(r => r.json())
      .then(d => {
        const list = (d.posts || [])
          .filter(p => p.published && p.pinned)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setPinned(list);
      })
      .catch(() => setPinned([]));
  }, []);

  // Until project posts are pinned via /write, fall back to the built-in set
  // so the homepage never renders an empty Projects section.
  const list = pinned && pinned.length ? pinned : DEFAULT_PROJECTS;

  return (
    <div className="projects-section" id="projects" ref={fadeRef}>
      <h3 className="section-title">Projects</h3>
      <div className="projects-grid">
        {list.map(p => <ProjectCard key={p.id} project={p} />)}
      </div>
    </div>
  );
};

const Writing = () => {
  const fadeRef = useFadeInOnScroll();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetch('/posts.json', { cache: 'no-store' })
      .then(r => r.json())
      .then(d => setPosts(
        (d.posts || [])
          .filter(p => p.published && p.kind !== 'project')
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 4)
      ))
      .catch(() => setPosts([]));
  }, []);

  if (!posts.length) return null;

  return (
    <div className="writing-section" id="writing" ref={fadeRef}>
      <h3 className="section-title">Writing</h3>
      <ul className="writing-list">
        {posts.map(p => (
          <li key={p.id}>
            <Link to="/posts">
              <span className="writing-date">
                {new Date(p.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
              </span>
              <span className="writing-title">{p.title}</span>
              <span className="writing-excerpt">{p.excerpt}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

const WorkExperience = () => {
  const fadeRef = useFadeInOnScroll();
  return (
    <div className="work-section" id="experience" ref={fadeRef}>
      <h3 className="section-title">Work Experience</h3>
      <div className="work-grid">
        <WorkCardEPRI />
        <WorkCard6 />
        <WorkCard1 />
        <WorkCard2 />
        <WorkCard3 />
        <WorkCard4 />
        <WorkCard5 />
      </div>
    </div>
  );
};

function WorkCard1() {
  const fadeRef = useFadeInOnScroll();
  return (
    <div className="work-card" ref={fadeRef}>
      <div className="work-header">
        <h4>Engineering Intern - Secure Systems</h4>
        <span className="work-date">Summer 2025</span>
      </div>
      <div className="work-content">
        <div className="work-company">
          <span className="company-name">TD Access</span>
        </div>
        <ul className="work-details">
          <li>Supported deployment and maintenance of <b>physical access control</b> for high-security sites including the FBI and Apple data centers.</li>
          <li>Worked with low-voltage control systems and network interfaces, reinforcing skills in <b>cyber-physical system integrity</b>.</li>
          <li>Gained practical experience with <b>security infrastructure</b>, control wiring, and real-time diagnostics in critical environments.</li>
        </ul>
        <div className="work-tech">
          <span className="tech-tag">Security</span>
          <span className="tech-tag">Access Control</span>
          <span className="tech-tag">Diagnostics</span>
        </div>
      </div>
    </div>
  );
}

function WorkCard2() {
  const fadeRef = useFadeInOnScroll();
  return (
    <div className="work-card" ref={fadeRef}>
      <div className="work-header">
        <h4>Engineering Research Assistant</h4>
        <span className="work-date">Oct 2023 - May 2024</span>
      </div>
      <div className="work-content">
        <div className="work-company">
          <span className="company-name">Dept. of Electrical and Computer Engineering - UNCC</span>
        </div>
        <ul className="work-details">
          <li>Cooperated with a team in designing and developing an Unmanned Aerial Vehicle (UAV).</li>
          <li>Engineered wireless data communications between multiple UAV controllers using UDP.</li>
        </ul>
        <div className="work-tech">
          <span className="tech-tag">UAV</span>
          <span className="tech-tag">UDP</span>
          <span className="tech-tag">Research</span>
        </div>
      </div>
    </div>
  );
}

function WorkCard3() {
  const fadeRef = useFadeInOnScroll();
  return (
    <div className="work-card" ref={fadeRef}>
      <div className="work-header">
        <h4>Mathematics and Engineering Tutor</h4>
        <span className="work-date">Jan 2025 - Current</span>
      </div>
      <div className="work-content">
        <div className="work-company">
          <span className="company-name">University Center for Academic Success - UNCC</span>
        </div>
        <ul className="work-details">
          <li>Helped engineering students raise average grades, including many recovering from academic probation.</li>
        </ul>
        <div className="work-tech">
          <span className="tech-tag">Tutoring</span>
          <span className="tech-tag">Mentoring</span>
          <span className="tech-tag">Communication</span>
        </div>
      </div>
    </div>
  );
}

function WorkCard4() {
  const fadeRef = useFadeInOnScroll();
  return (
    <div className="work-card" ref={fadeRef}>
      <div className="work-header">
        <h4>Technical Support Specialist - AV & IT Systems</h4>
        <span className="work-date">Aug 2023 - May 2025</span>
      </div>
      <div className="work-content">
        <div className="work-company">
          <span className="company-name">Office of Learning Space Technology - UNCC</span>
        </div>
        <ul className="work-details">
          <li>Supported setup and maintenance of AV/IT systems for 200+ campus classrooms, ensuring reliable networking and
media performance.</li>
        </ul>
        <div className="work-tech">
          <span className="tech-tag">AV</span>
          <span className="tech-tag">IT Support</span>
          <span className="tech-tag">Troubleshooting</span>
        </div>
      </div>
    </div>
  );
}

function WorkCard5() {
  const fadeRef = useFadeInOnScroll();
  return (
    <div className="work-card" ref={fadeRef}>
      <div className="work-header">
        <h4>Hiring and Training Director</h4>
        <span className="work-date">Dec 2017 - Sep 2025</span>
      </div>
      <div className="work-content">
        <div className="work-company">
          <span className="company-name">Chick-fil-A, Monroe</span>
        </div>
        <ul className="work-details">
          <li>Lead training and onboarding for new team members, improving operational efficiency and retention.</li>
          <li>Directed hiring and recruitment, screening candidates and conducting interviews to build high-performing teams.</li>
        </ul>
        <div className="work-tech">
          <span className="tech-tag">Leadership</span>
          <span className="tech-tag">Hiring</span>
          <span className="tech-tag">Training</span>
        </div>
      </div>
    </div>
  );
}

function WorkCardEPRI() {
  const fadeRef = useFadeInOnScroll();
  return (
    <div className="work-card" ref={fadeRef}>
      <div className="work-header">
        <h4>Electrical/Mechatronics Engineer I</h4>
        <span className="work-date">Mar 2026 - Present</span>
      </div>
      <div className="work-content">
        <div className="work-company">
          <span className="company-name">Electric Power Research Institute (EPRI)</span>
        </div>
        <ul className="work-details">
          <li>Conducting applied R&amp;D on advanced <b>T&amp;D asset monitoring systems</b> for the electric utility industry, supporting grid reliability.</li>
          <li>Programming and testing <b>embedded systems</b> for data acquisition in harsh electrical and environmental field conditions.</li>
          <li>Performing <b>electrical and environmental testing</b> and mechanical packaging of ruggedized electronic systems.</li>
          <li>Collaborating with lead engineers and <b>utility stakeholders</b> to enhance reliability of critical grid infrastructure.</li>
        </ul>
        <div className="work-tech">
          <span className="tech-tag">Embedded Systems</span>
          <span className="tech-tag">T&amp;D Monitoring</span>
          <span className="tech-tag">Grid Infrastructure</span>
          <span className="tech-tag">R&amp;D</span>
        </div>
      </div>
    </div>
  );
}

function WorkCard6() {
  const fadeRef = useFadeInOnScroll();
  return (
    <div className="work-card" ref={fadeRef}>
      <div className="work-header">
        <h4>Engineering Intern - Automation</h4>
        <span className="work-date">Aug 2025 - Jan 2026</span>
      </div>
      <div className="work-content">
        <div className="work-company">
          <span className="company-name">Schweitzer Engineering Laboratories (SEL)</span>
        </div>
        <ul className="work-details">
          <li>Designed, configured, and tested <b>automation, SCADA, and HMI systems</b> for utility-scale power infrastructure.</li>
          <li>Developed and validated <b>relay settings, PLC logic, and substation automation</b> workflows in safety-critical environments.</li>
          <li>Implemented and tested communication logic using <b>UDP/TCP protocols in Structured Text</b> for device interoperability.</li>
          <li>Participated in system integration, validation testing, and failure analysis with emphasis on <b>ICS constraints and cybersecurity awareness</b>.</li>
        </ul>
        <div className="work-tech">
          <span className="tech-tag">SCADA</span>
          <span className="tech-tag">Automation</span>
          <span className="tech-tag">UDP/TCP</span>
          <span className="tech-tag">Industrial Cybersecurity</span>
        </div>
      </div>
    </div>
  );
}

const ContactForm = () => {
  const fadeRef = useFadeInOnScroll();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, submitted: false, error: null });

    try {
      console.log({
        name: formData.name,
        email: formData.email,
        title: formData.subject,
        message: formData.message
      });

      await emailjs.send(
        'service_dnl87p6',         // Service ID
        'template_cq4pjds',        // Template ID
        {
          name: formData.name,
          email: formData.email,
          title: formData.subject,
          message: formData.message,
        },
        'igqKHjGu11kVt3ckI'        // Public Key
      );

      setStatus({
        submitting: false,
        submitted: true,
        error: null
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('EmailJS error:', error);
      setStatus({
        submitting: false,
        submitted: false,
        error: 'Failed to send message. Please try again later.'
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="contact-form-section" id="contact" ref={fadeRef}>
      <h3 className="section-title">Get in Touch</h3>
      {status.submitted ? (
        <div className="success-message">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 2 6"></polyline>
          </svg>
          <p>Thank you for your message! I'll get back to you soon.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="contact-form">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={status.submitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={status.submitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="subject">Subject</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              disabled={status.submitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="5"
              disabled={status.submitting}
            />
          </div>
          {status.error && (
            <div className="error-message">
              {status.error}
            </div>
          )}
          <button 
            type="submit" 
            className="submit-button"
            disabled={status.submitting}
          >
            {status.submitting ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      )}
    </div>
  );
};

const MobileSidebarSection = () => {
  const fadeRef = useFadeInOnScroll();
  return (
    <section className="mobile-sidebar-section mobile-only" id="mobile-contact" ref={fadeRef}>
      <div className="contact-card">
        <div className="contact-details">
          <div className="contact-item">
            <span className="label">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              Location
            </span>
            <span className="value">Charlotte, North Carolina</span>
          </div>
          <div className="contact-item">
            <span className="label">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
              Email
            </span>
            <a href="mailto:me@robertengineer.com">me@robertengineer.com</a>
          </div>
          <div className="contact-item">
            <span className="label">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              Resume
            </span>
            <a href="/RobertThomas.pdf" target="_blank" rel="noopener noreferrer">View Resume</a>
          </div>
        </div>
      </div>
      <div className="social-card">
        <h3>Connect With Me</h3>
        <div className="social-links">
          <a href="https://www.linkedin.com/in/robertengineer" target="_blank" rel="noopener noreferrer" className="social-link">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
              <rect x="2" y="9" width="4" height="12"></rect>
              <circle cx="4" cy="4" r="2"></circle>
            </svg>
            LinkedIn
          </a>
          <a href="https://github.com/robertengineerr" target="_blank" rel="noopener noreferrer" className="social-link">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
            </svg>
            GitHub
          </a>
          <a href="https://robertengineer.substack.com/" target="_blank" rel="noopener noreferrer" className="social-link">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
            </svg>
            Substack (Blog)
          </a>
        </div>
      </div>
    </section>
  );
};

const MainContent = () => (
  <div className="main-content">
    <Projects />
    <div id="home" className="content-grid">
      <div className="profile-section">
        <div className="profile-info about-me-centered">
          <h3 className="card-heading">About Me</h3>
            <p>
              I’m Robert Thomas, a Computer Engineering graduate from UNC Charlotte (Magna Cum Laude, IEEE-HKN) with a minor in Mathematics.
              I work on projects that tie together embedded systems, power infrastructure, and software.
              <br /><br />
              I’m currently an Electrical/Mechatronics Engineer at the Electric Power Research Institute (EPRI), conducting applied R&amp;D on
              T&amp;D asset monitoring systems, programming embedded systems for harsh field environments and working with utility stakeholders
              to improve grid reliability.
              <br /><br />
              I like working on practical, mission-driven projects. In my free time I build things, study French, read, and play guitar.
            </p>
        </div>
        <div className="education-section">
          <EducationCard />
        </div>
      </div>
      {/* Sidebar for desktop only */}
      <div className="sidebar desktop-sidebar">
        <div className="profile-image-card">
          <img className="profile-image" src="/images/ProfessionalPhoto.jpeg" alt="Robert Thomas" />
        </div>
        <div className="contact-card">
          <div className="contact-details">
            <div className="contact-item">
              <span className="label">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                Location
              </span>
              <span className="value">Charlotte, North Carolina</span>
            </div>
            <div className="contact-item">
              <span className="label">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                Email
              </span>
              <a href="mailto:me@robertengineer.com">me@robertengineer.com</a>
            </div>
            <div className="contact-item">
              <span className="label">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                Resume
              </span>
              <a href="/RobertThomas.pdf" target="_blank" rel="noopener noreferrer">View Resume</a>
            </div>
          </div>
        </div>

        <div className="social-card">
          <h3>Connect With Me</h3>
          <div className="social-links">
            <a href="https://www.linkedin.com/in/robertengineer" target="_blank" rel="noopener noreferrer" className="social-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                <rect x="2" y="9" width="4" height="12"></rect>
                <circle cx="4" cy="4" r="2"></circle>
              </svg>
              LinkedIn
            </a>
            <a href="https://github.com/robertengineerr" target="_blank" rel="noopener noreferrer" className="social-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"></path>
              </svg>
              GitHub
            </a>
            <a href="https://robertengineer.substack.com/" target="_blank" rel="noopener noreferrer" className="social-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
              </svg>
              Substack (Blog)
            </a>
          </div>
        </div>

        <div className="contact-button">
          <a href="#contact" className="contact-link">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            Contact Me
          </a>
        </div>
      </div>
    </div>
    {/* Full-width sections for desktop */}
    <Writing />
    <WorkExperience />
    <SkillsSection />
    <MobileSidebarSection />
    <ContactForm />
  </div>
);

function EducationCard() {
  const fadeRef = useFadeInOnScroll();
  return (
    <div className="education-card" ref={fadeRef}>
      <div className="education-header">
        <h4>Bachelor of Science in Computer Engineering</h4>
        <span className="date">Dec 2025</span>
      </div>
      <div className="education-details">
        <div className="university">University of North Carolina at Charlotte</div>
        <ul className="education-list">
          <li><b>GPA:</b> 3.8</li>
          <li><b>Minor:</b> Mathematics</li>
          <li><b>Honors:</b> IEEE HKN, Chancellor's/Dean's List, Honors College</li>
        </ul>
        <p>This program focuses on the study of computer systems, hardware, and software development.</p>
      </div>
    </div>
  );
}

function SkillsSection() {
  const fadeRef = useFadeInOnScroll();
  return (
    <div className="skills-section" ref={fadeRef}>
      <h3 className="section-title">Skills & Technologies</h3>
      <ul>
        <li><strong>Languages:</strong> Python, C++, JavaScript, HTML, CSS</li>
        <li><strong>Hardware:</strong> Arduino, Raspberry Pi, PCB Design, Soldering</li>
        <li><strong>Tools:</strong> Git, Linux, VS Code, MATLAB, Simulink</li>
        <li><strong>Frameworks & Libraries:</strong> React, NumPy, Matplotlib, PyTorch, TensorFlow</li>
        <li><strong>Cloud / DevOps:</strong> AWS (basic), MQTT, Modbus TCP/IP</li>
      </ul>
    </div>
  );
}

const MobileNav = ({ isDarkMode, toggleDarkMode }) => {
  return (
    <nav className="mobile-nav">
      <a href="#home" className="mobile-nav-item">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
        <span>Home</span>
      </a>
      <a href="#projects" className="mobile-nav-item">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
        </svg>
        <span>Projects</span>
      </a>
      <a href="#mobile-contact" className="mobile-nav-item">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        <span>Contact Me</span>
      </a>
      <a href="/RobertThomas.pdf" target="_blank" rel="noopener noreferrer" className="mobile-nav-item">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
        </svg>
        <span>Resume</span>
      </a>
    </nav>
  );
};

const App = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      return JSON.parse(savedMode);
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    document.body.classList.toggle('dark-mode', isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (localStorage.getItem('darkMode') === null) {
        setIsDarkMode(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const [hasPosts, setHasPosts] = useState(false);
  const [hasStories, setHasStories] = useState(false);
  useEffect(() => {
    fetch('/posts.json', { cache: 'no-store' }).then(r => r.json()).then(d => setHasPosts((d.posts || []).some(p => p.published))).catch(() => {});
    fetch('/stories.json', { cache: 'no-store' }).then(r => r.json()).then(d => setHasStories((d.posts || []).some(p => p.published))).catch(() => {});
  }, []);

  // Intersection Observer for Contact Me button
  const [showContactCTA, setShowContactCTA] = useState(false);
  useEffect(() => {
    if (window.innerWidth < 1025) return; // Only on desktop
    const contactBtn = document.querySelector('.desktop-sidebar .contact-button');
    if (!contactBtn) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setShowContactCTA(!entry.isIntersecting),
      { root: null, threshold: 0 }
    );
    observer.observe(contactBtn);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="app">
      <Helmet>
        <title>Robert Thomas</title>
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="shortcut icon" type="image/x-icon" href="/favicon.ico" />
      </Helmet>

      <Navigation isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} showContactCTA={showContactCTA} hasPosts={hasPosts} hasStories={hasStories} />
      <MobileNav isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
      <Routes>
        <Route path="/" element={
          <div className="container">
            <MobileHeaderCard />
            <Header />
            <MainContent />
          </div>
        } />
        <Route path="/posts" element={<Posts />} />
        <Route path="/stories" element={<Stories />} />
        <Route path="/write" element={<Write />} />
      </Routes>
    </div>
  );
};

export default App;

